import React, { useState, useEffect } from 'react';
import { useJobs } from '../services/JobContext';
import { useAuth } from '../contexts/AuthContext';
import { canAddFeedback } from '../utils/permissions';
import api from '../services/api';

import { format } from 'date-fns';

const JobDetails = ({ job, onClose }) => {
  const { getJobInterviews, updateInterview, updateJob, jobs } = useJobs();
  const { user } = useAuth();
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [feedback, setFeedback] = useState('');
  const [takenBy, setTakenBy] = useState('');
  const [interviewTakers, setInterviewTakers] = useState([]);
  const [currentJob, setCurrentJob] = useState(job);
  const [loadingDocument, setLoadingDocument] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    const fetchInterviewTakers = async () => {
      try {
        const takers = await api.request('/api/v1/interview-takers');
        setInterviewTakers(takers || []);
      } catch (error) {
        console.error('Failed to load interview takers in JobDetails', error);
      }
    };

    fetchInterviewTakers();
  }, []);

  // Update currentJob when jobs context changes (e.g., after file upload)
  useEffect(() => {
    const updatedJob = jobs.find(j => j.id === job.id);
    if (updatedJob) {
      setCurrentJob(updatedJob);
    }
  }, [jobs, job.id]);


  const jobInterviews = getJobInterviews(currentJob.id);

  // Offer awaiting: a round flagged as final at scheduling has been converted
  const isFinalFlagTrue = (v) => v === true || v === 'true' || v === 1 || v === '1';
  const isOfferAwaiting = jobInterviews.some(
    (i) => isFinalFlagTrue(i.isFinalRound) && i.status === 'converted'
  );

  const handleStatusUpdate = async (newStatus) => {
    if (newStatus === currentJob.status) return;
    setUpdatingStatus(true);
    try {
      await updateJob(currentJob.id, { ...currentJob, status: newStatus });
      setCurrentJob({ ...currentJob, status: newStatus });
    } catch (error) {
      console.error('Failed to update job status', error);
      alert('Could not update job status. Please try again.');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const openFeedbackEditor = (interview) => {
    setSelectedInterview(interview.id);
    setFeedback(interview.feedback || '');
    setTakenBy(interview.takenBy || '');
  };

  const handleOpenDocument = async (fileUrl, docType) => {
    setLoadingDocument(docType);
    try {
      const response = await api.request('/api/v1/jobs/generate-presigned-url', {
        method: 'POST',
        body: JSON.stringify({ file_url: fileUrl })
      });
      
      // Open pre-signed URL in new tab
      window.open(response.presigned_url, '_blank');
    } catch (error) {
      console.error('Failed to generate pre-signed URL:', error);
      alert('Failed to open document. Please try again.');
    } finally {
      setLoadingDocument(null);
    }
  };

  const handleAddFeedback = async (interviewId) => {
    if (!feedback.trim() || !takenBy) {
      alert('Please provide feedback and select who took the interview');
      return;
    }

    await updateInterview(interviewId, {
      feedback,
      takenBy,
      feedbackDate: new Date().toISOString()
    });

    setFeedback('');
    setTakenBy('');
    setSelectedInterview(null);
    alert('Feedback added successfully!');
  };

  return (
    <div className="modal">
      <div 
        className="modal-content"
        style={{
          maxWidth: '600px',
          width: '100%',
          maxHeight: '85vh',
          overflowY: 'auto',
          borderRadius: '12px',
          padding: '0'
        }}
      >
        {/* Header */}
        <div 
          className="modal-header"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 20px',
            borderBottom: '1px solid #e2e8f0',
            position: 'sticky',
            top: 0,
            background: 'white',
            zIndex: 1
          }}
        >
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#2d3748' }}>
            Job Details
          </h2>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        <div style={{ padding: '20px' }}>
          {/* Job Info Card */}
          <div style={{ 
            background: '#f8f9fa',
            padding: '16px',
            borderRadius: '8px',
            marginBottom: '20px',
            borderLeft: '4px solid #38a169'
          }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: 600, color: '#2d3748' }}>
              {currentJob.companyName} - {currentJob.position}
            </h3>
            <p style={{ margin: '0 0 12px 0', color: '#718096', fontSize: '13px' }}>
              BD: {currentJob.bdName}
              {currentJob.createdAt && !isNaN(new Date(currentJob.createdAt)) && (
                <> • Created {format(new Date(currentJob.createdAt), 'MMM dd, yyyy')}</>
              )}
            </p>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
              <div>
                <p style={{ margin: '0 0 2px 0', color: '#718096', fontSize: '11px', textTransform: 'uppercase' }}>Job Type</p>
                <p style={{ margin: 0, color: '#2d3748', fontWeight: 500, fontSize: '14px' }}>{currentJob.jobType}</p>
              </div>
              <div>
                <p style={{ margin: '0 0 2px 0', color: '#718096', fontSize: '11px', textTransform: 'uppercase' }}>Pay Range</p>
                <p style={{ margin: 0, color: '#2d3748', fontWeight: 500, fontSize: '14px' }}>{currentJob.payRange || 'N/A'}</p>
              </div>
              <div>
                <p style={{ margin: '0 0 2px 0', color: '#718096', fontSize: '11px', textTransform: 'uppercase' }}>Status</p>
                <span className={`status-badge status-${currentJob.status}`}>
                  {currentJob.status}
                </span>
              </div>
            </div>
          </div>

          {/* Offer Awaiting — Final Decision panel */}
          {isOfferAwaiting && (
            <div
              style={{
                position: 'relative',
                padding: '16px 18px',
                marginBottom: '20px',
                background:
                  'linear-gradient(135deg, var(--color-warning-bg) 0%, rgba(255,255,255,0.4) 70%, var(--surface-card) 100%)',
                border: '1px solid var(--color-warning-border)',
                borderRadius: '10px',
                boxShadow: '0 0 0 1px var(--color-warning-border)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '12px',
                  marginBottom: '10px',
                }}
              >
                <div>
                  <p
                    style={{
                      margin: 0,
                      fontSize: '11px',
                      fontWeight: 700,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      color: 'var(--color-warning)',
                    }}
                  >
                    Offer awaiting
                  </p>
                  <h4
                    style={{
                      margin: '2px 0 0 0',
                      fontSize: '15px',
                      fontWeight: 600,
                      color: 'var(--text-primary)',
                    }}
                  >
                    Final decision needed for this job
                  </h4>
                </div>
                <span className={`status-badge status-${currentJob.status}`}>
                  {currentJob.status}
                </span>
              </div>
              <p
                style={{
                  margin: '0 0 12px 0',
                  fontSize: '13px',
                  color: 'var(--text-secondary)',
                  lineHeight: 1.5,
                }}
              >
                The final-round interview has been converted. Update the job
                status to record the outcome.
              </p>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button
                  className="btn btn-success btn-sm"
                  disabled={updatingStatus || currentJob.status === 'completed'}
                  onClick={() => handleStatusUpdate('completed')}
                >
                  {currentJob.status === 'completed'
                    ? '✓ Marked completed'
                    : 'Mark as completed'}
                </button>
                <button
                  className="btn btn-danger btn-sm"
                  disabled={updatingStatus || currentJob.status === 'rejected'}
                  onClick={() => handleStatusUpdate('rejected')}
                >
                  {currentJob.status === 'rejected'
                    ? '✓ Marked rejected'
                    : 'Mark as rejected'}
                </button>
                <button
                  className="btn btn-secondary btn-sm"
                  disabled={updatingStatus || currentJob.status === 'waiting'}
                  onClick={() => handleStatusUpdate('waiting')}
                >
                  {currentJob.status === 'waiting'
                    ? '✓ Marked waiting'
                    : 'Mark as waiting'}
                </button>
              </div>
            </div>
          )}

          {/* Documents */}
          <div style={{ marginBottom: '20px' }}>
            <p style={{ margin: '0 0 10px 0', color: '#718096', fontSize: '12px', textTransform: 'uppercase', fontWeight: 600 }}>Documents</p>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              {(currentJob.resumeLink || currentJob.resume_link) && (
                <button
                  onClick={() => handleOpenDocument(currentJob.resumeLink || currentJob.resume_link, 'resume')}
                  disabled={loadingDocument === 'resume'}
                  style={{ 
                    color: '#38a169', 
                    fontWeight: 500,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 14px',
                    background: 'rgba(56, 161, 105, 0.1)',
                    borderRadius: '6px',
                    border: '1px solid rgba(56, 161, 105, 0.3)',
                    fontSize: '13px',
                    cursor: loadingDocument === 'resume' ? 'wait' : 'pointer'
                  }}
                >
                  {loadingDocument === 'resume' ? '⏳ Loading...' : '📄 Resume'}
                </button>
              )}
              
              {(currentJob.jdLink || currentJob.jd_link) && (
                <button
                  onClick={() => handleOpenDocument(currentJob.jdLink || currentJob.jd_link, 'jd')}
                  disabled={loadingDocument === 'jd'}
                  style={{ 
                    color: '#38a169', 
                    fontWeight: 500,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 14px',
                    background: 'rgba(56, 161, 105, 0.1)',
                    borderRadius: '6px',
                    border: '1px solid rgba(56, 161, 105, 0.3)',
                    fontSize: '13px',
                    cursor: loadingDocument === 'jd' ? 'wait' : 'pointer'
                  }}
                >
                  {loadingDocument === 'jd' ? '⏳ Loading...' : '📋 Job Description'}
                </button>
              )}
              
              {!(currentJob.resumeLink || currentJob.resume_link) && !(currentJob.jdLink || currentJob.jd_link) && (
                <p style={{ color: '#a0aec0', fontStyle: 'italic', fontSize: '13px', margin: 0 }}>No documents uploaded</p>
              )}
            </div>
          </div>

          {/* Interview History */}
          <div>
            <p style={{ margin: '0 0 12px 0', color: '#718096', fontSize: '12px', textTransform: 'uppercase', fontWeight: 600 }}>
              Interview History ({jobInterviews.length})
            </p>
            
            {jobInterviews.length === 0 ? (
              <p style={{ color: '#a0aec0', fontSize: '13px' }}>No interviews scheduled yet.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {jobInterviews.map(interview => (
                  <div 
                    key={interview.id} 
                    style={{ 
                      padding: '14px',
                      background: '#f8f9fa',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0'
                    }}
                  >
                    {/* Interview Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <h5 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#2d3748' }}>
                        Round {interview.round} - {interview.interviewType}
                      </h5>
                      <span className={`status-badge status-${interview.status || 'waiting'}`}>
                        {interview.status || 'waiting'}
                      </span>
                    </div>
                    
                    {/* Interview Details Grid */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '13px', marginBottom: '10px' }}>
                      <div>
                        <span style={{ color: '#718096' }}>Date: </span>
                        <span style={{ color: '#2d3748', fontWeight: 500 }}>
                          {format(new Date(interview.dateTime), 'MMM dd, yyyy')}
                        </span>
                      </div>
                      <div>
                        <span style={{ color: '#718096' }}>Time: </span>
                        <span style={{ color: '#2d3748', fontWeight: 500 }}>
                          {format(new Date(interview.dateTime), 'hh:mm a')}
                        </span>
                      </div>
                      <div>
                        <span style={{ color: '#718096' }}>Duration: </span>
                        <span style={{ color: '#2d3748', fontWeight: 500 }}>{interview.duration} min</span>
                      </div>
                      {interview.recruiterName && (
                        <div>
                          <span style={{ color: '#718096' }}>Recruiter: </span>
                          <span style={{ color: '#2d3748', fontWeight: 500 }}>{interview.recruiterName}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Feedback Section */}
                    {interview.feedback ? (
                      <div style={{ 
                        padding: '10px 12px',
                        background: '#d4edda',
                        borderRadius: '6px',
                        marginBottom: canAddFeedback(user?.role) ? '10px' : '0'
                      }}>
                        <p style={{ margin: '0 0 4px 0', fontWeight: 600, color: '#155724', fontSize: '12px' }}>✓ Feedback</p>
                        <p style={{ margin: '0 0 2px 0', fontSize: '12px', color: '#155724' }}>
                          <strong>By:</strong> {interview.takenBy}
                        </p>
                        <p style={{ margin: 0, fontSize: '13px', color: '#155724' }}>{interview.feedback}</p>
                      </div>
                    ) : (
                      <div style={{ 
                        padding: '10px 12px',
                        background: '#fff3cd',
                        borderRadius: '6px',
                        marginBottom: canAddFeedback(user?.role) ? '10px' : '0'
                      }}>
                        <p style={{ margin: 0, color: '#856404', fontSize: '12px' }}>⏳ Feedback pending</p>
                      </div>
                    )}

                    {/* Add/Edit Feedback Button */}
                    {canAddFeedback(user?.role) && selectedInterview !== interview.id && (
                      <button
                        className="btn btn-primary"
                        style={{ fontSize: '13px', padding: '6px 12px' }}
                        onClick={() => openFeedbackEditor(interview)}
                      >
                        {interview.feedback ? 'Edit Feedback' : 'Add Feedback'}
                      </button>
                    )}

                    {/* Feedback Editor */}
                    {selectedInterview === interview.id && canAddFeedback(user?.role) && (
                      <div style={{ 
                        marginTop: '12px', 
                        padding: '14px', 
                        backgroundColor: 'white', 
                        borderRadius: '6px',
                        border: '1px solid #e2e8f0'
                      }}>
                        <p style={{ margin: '0 0 12px 0', fontWeight: 600, fontSize: '13px', color: '#2d3748' }}>Add Feedback</p>
                        
                        <div style={{ marginBottom: '12px' }}>
                          <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#718096' }}>Taken by</label>
                          <select
                            value={takenBy}
                            onChange={(e) => setTakenBy(e.target.value)}
                            style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '13px' }}
                          >
                            <option value="">Select interviewer</option>
                            {interviewTakers.map((taker) => (
                              <option key={taker.id} value={taker.name}>{taker.name}</option>
                            ))}
                          </select>
                        </div>

                        <div style={{ marginBottom: '12px' }}>
                          <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#718096' }}>Feedback</label>
                          <textarea
                            value={feedback}
                            onChange={(e) => setFeedback(e.target.value)}
                            rows="3"
                            placeholder="Enter interview feedback..."
                            style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '13px', resize: 'vertical' }}
                          />
                        </div>

                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            className="btn btn-success"
                            style={{ fontSize: '13px', padding: '6px 12px' }}
                            onClick={() => handleAddFeedback(interview.id)}
                          >
                            Save
                          </button>
                          <button
                            className="btn"
                            style={{ fontSize: '13px', padding: '6px 12px' }}
                            onClick={() => {
                              setSelectedInterview(null);
                              setFeedback('');
                              setTakenBy('');
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{ 
          padding: '16px 20px', 
          borderTop: '1px solid #e2e8f0',
          position: 'sticky',
          bottom: 0,
          background: 'white'
        }}>
          <button 
            className="btn btn-primary"
            style={{ width: '100%' }}
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default JobDetails;